import React, { useState, useContext } from "react";
import { useNavigate } from "react-router";
import AppSettings from "../../../../Settings/AppSettings";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import LoadingSuccessErrorAlert from "../../Rules/Components/LoadingSuccessErrorAlert";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { AppContext } from "../../../../Context/AppContext";
import PdwUploadDropzone from "./PdwUploadDropzone";
import { RulesContext } from "Components/GMS/Rules/RulesContext";
import { RULE_SUCCESS_MESSAGE } from "Components/GMS/Rules/Components/RuleConstants";

/**
 * PDW "Product Modification File" upload — and, when `type === "CBM"`, the Rules
 * matrix commit. On the design system since § 126: the markup moved into
 * `PdwUploadDropzone`, every handler below is byte-for-byte what it was.
 *
 * The CBM branch of `checkUploadStatus` is the sensitive one — it sets
 * `RulesContext.allowNavigationRef` before navigating so `RuleHeader`'s
 * unsaved-changes blocker does not intercept (`okf/rules/e2e.md` § 7). Untouched.
 */
const Update = ({ modalUpdateClose, load_Products_By_Category, fromProduct = false, type = null, ruleId = null, display, fetchData, instanceId = "pdw-update" }) => {
  const appData = useContext(AppContext);
  const rulesContextData = useContext(RulesContext);
  const channelKey = appData?.channelKey;
  const navigate = useNavigate();
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const [Loading, setLoading] = useState({ status: "", message: "", });
  const suppressRule = false;
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [inputKey, setInputKey] = useState("");

  // delete selected file
  async function handleFileDelete(event) {
    event.preventDefault();
    if (selectedFile != null) setSelectedFile(null);
  }

  async function handle_file_selection(event) {
    event.preventDefault();
    const file = event.target.files[0];
    const totalCount = await appData.checkSelectedFile(file, file.type);
    if (totalCount > 0) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  }

  const do_upload_pdw = async () => {
    try {
      setLoading({ status: "Loading", message: "File uploading....", });
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("supress_rule", suppressRule);
      const url = type === "CBM" ? (display === 'values' ? `/rule/attribute_values/commit_rule/${ruleId}` : `rule/products/commit_rule/${ruleId}`) : `pdw/upload/updateproducts/${channelKey}`;
      const res = await ServiceWrapper.doPost(api_base_url + url, formData, null, AuthManager.getGmsToken(), true);

      if (!res.errorfound) {
        if (res.data.done === true) {
          setLoading({ status: "Loading", message: res.data.message, });
          setTimeout(() => {
            const jobId = type === "CBM" ? res.data.job_id : res.data.jobid;
            checkUploadStatus(jobId);
          }, 2000);
        } else {
          toast.error(res.data.message);
          setLoading({ status: "Error", message: res.data.message, });
        }
      } else {
        toast.error("Error while updating...");
        setLoading({ status: "Error", message: "Error Updating ", });
      }
      functionThatResetsTheFileInput();
      setSelectedFile(null);
    } catch (error) {
      console.error("constdo_upload_pdw= ~ error:", error)
    }
  }

  const checkUploadStatus = async (jobId) => {
    try {
      if (jobId) {
        const jobStatusResponse = await ServiceWrapper.doGet(api_base_url + "job/status/" + jobId, null, null, AuthManager.getGmsToken());

        if (!jobStatusResponse.errorfound) {
          if (jobStatusResponse.data.processing) {
            setLoading({ status: "Loading", message: jobStatusResponse.data.status });
            setTimeout(() => { checkUploadStatus(jobId); }, 2000);
            return;
          }

          if (jobStatusResponse.data.error) {
            toast.error(jobStatusResponse.data.status);
            setLoading({ status: "Error", message: jobStatusResponse.data.status });
            return;
          }

          if (fromProduct) {
            load_Products_By_Category();
          }
          if (type === 'CBM') {
            if (jobStatusResponse?.data?.message === RULE_SUCCESS_MESSAGE) {
              toast.success("Updated successfully");
              /* § 116: the JSX wrapper existed to hand-set a green background and a
                 font the toast already uses. The DS `success` tone does both. */
              toast.success("Committing in progress");
              // This commit already succeeded server-side; tell RuleHeader's
              // unsaved-changes blocker (shared via RulesContext) not to also
              // pop the "Unsaved Changes" modal for this navigation.
              if (rulesContextData?.allowNavigationRef) {
                rulesContextData.allowNavigationRef.current = true;
              }
              navigate("/gms/compatibilities");
            } else {
              toast.success(jobStatusResponse?.data?.message);
              fetchData();
            }
          } else {
            toast.success(jobStatusResponse.data.status);
          }
          setLoading({ status: "", message: "" });
          setTimeout(() => {
            modalUpdateClose();
          }, 2000);
        }
      } else {
        toast.error("Job Id not found");
        setLoading({ status: "Error", message: "JobId not found" });
      }
    } catch (error) {
      console.error("checkUploadStatus ~ error:", error)
    }
  }

  const functionThatResetsTheFileInput = () => {
    let randomString = Math.random().toString(36);
    setInputKey(randomString);
  };

  return (
    <div>
      <PdwUploadDropzone
        inputId={`${instanceId}-file`}
        inputTestId="upload-update-input-1"
        inputKey={inputKey}
        onFileSelect={(event) => handle_file_selection(event)}
        selectedFile={selectedFile}
        onDelete={handleFileDelete}
        onCancel={() => modalUpdateClose()}
        onSave={() => do_upload_pdw()}
        cancelTestId="pdw-update-cancel-btn"
        /* Update had NO Save testid — asymmetric with Create, flagged in
           `okf/pdw/e2e.md` § 4 and fixed here. */
        saveTestId="pdw-update-save-btn"
        deleteTestId="pdw-update-delete-btn"
        /* No `Loading === "Loading"` term, unlike Create. Preserved as-is: the
           asymmetry is real and pre-existing, and tightening it here would change
           the CBM commit's behaviour in a migration commit. */
        saveDisabled={!selectedFile}
      />

      <div className="tw-mt-4">
        <LoadingSuccessErrorAlert loading={Loading} hide_error={true} />
      </div>
    </div>
  );
};
export default Update;